import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
	Table,
	TableBody,
	TableCell,
  TableContainer,
	TableHead,
	TableRow,
  Paper,
  TablePagination,
  Chip,
  Grid,
  InputAdornment,
  Tooltip,
  IconButton,
	Dialog,
  DialogTitle,
	DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Policy as PolicyIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const Policy = () => {
	// State management
  const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
	const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

	// Form state
  const [newPolicy, setNewPolicy] = useState({
		policyName: '',
    policyType: '',
		description: '',
    effectiveDate: '',
    expiryDate: '',
		status: 'active',
	});

  // Mock policy data
	const [policies, setPolicies] = useState([
		{
			id: 1,
			policyName: 'Leave Policy',
      policyType: 'HR',
      description: 'Guidelines for employee leave and vacation.',
      effectiveDate: '2024-01-01',
      expiryDate: '2024-12-31',
			status: 'active',
      createdDate: '2023-12-01',
		},
		{
			id: 2,
      policyName: 'Security Policy',
      policyType: 'IT',
      description: 'Information security and data protection guidelines.',
      effectiveDate: '2024-01-01',
      expiryDate: '2025-12-31',
			status: 'active',
      createdDate: '2023-11-15',
		},
		{
			id: 3,
      policyName: 'Travel Policy',
      policyType: 'Finance',
      description: 'Business travel and expense reimbursement policy.',
      effectiveDate: '2023-06-01',
      expiryDate: '2023-12-31',
      status: 'expired',
      createdDate: '2023-05-20',
		},
	]);

	// Filter policies
	const filteredPolicies = policies.filter((policy) => {
    const matchesSearch = policy.policyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.policyType.toLowerCase().includes(searchQuery.toLowerCase());
		const matchesStatus = statusFilter === 'all' || policy.status === statusFilter;
		return matchesSearch && matchesStatus;
	});

  // Pagination
  const paginatedPolicies = filteredPolicies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
  };

  const handleOpenModal = () => {
    setNewPolicy({
			policyName: '',
      policyType: '',
			description: '',
      effectiveDate: '',
      expiryDate: '',
			status: 'active',
		});
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPolicy(null);
  };

  const handleView = (policy) => {
    setSelectedPolicy(policy);
    setOpenViewModal(true);
  };

  const handleEdit = (policy) => {
    setSelectedPolicy(policy);
    setNewPolicy(policy);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
		setIsLoading(true);
		try {
			await new Promise(resolve => setTimeout(resolve, 1000));
        setPolicies(policies.filter((policy) => policy.id !== id));
        setSuccessMessage('Policy deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
		} catch (error) {
        setErrors({ submit: 'Failed to delete policy' });
		} finally {
			setIsLoading(false);
      }
    }
  };

  const handleSavePolicy = async () => {
    const validationErrors = {};
    if (!newPolicy.policyName) validationErrors.policyName = 'Policy name is required';
    if (!newPolicy.policyType) validationErrors.policyType = 'Policy type is required';
    if (!newPolicy.effectiveDate) validationErrors.effectiveDate = 'Effective date is required';

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
			return;
		}

		setIsLoading(true);
		try {
			await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (selectedPolicy) {
        // Update existing
        setPolicies(policies.map((policy) =>
          policy.id === selectedPolicy.id ? { ...policy, ...newPolicy } : policy
        ));
        setSuccessMessage('Policy updated successfully!');
      } else {
        // Add new
        const newPol = {
          ...newPolicy,
          id: Date.now(),
          createdDate: new Date().toISOString().split('T')[0],
        };
        setPolicies([...policies, newPol]);
        setSuccessMessage('Policy added successfully!');
      }

      handleCloseModal();
      setTimeout(() => setSuccessMessage(''), 3000);
		} catch (error) {
      setErrors({ submit: 'Failed to save policy' });
		} finally {
			setIsLoading(false);
		}
	};

  const getStatusStyle = (status) => {
    if (status === 'active') return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    if (status === 'expired') return { backgroundColor: '#ffebee', color: '#c62828' };
    return { backgroundColor: '#fff3e0', color: '#e65100' };
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PolicyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Policy Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage organization policies and guidelines
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{ textTransform: 'none' }}
        >
									Add Policy
								</Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
									placeholder="Search policies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
							</Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
							<Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ textTransform: 'none' }}
              >
                Search
							</Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ textTransform: 'none' }}
              >
                Reset
								</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
							<Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Policy Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Effective Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expiry Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPolicies.length === 0 ? (
									<TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No policies found
                    </Typography>
                  </TableCell>
									</TableRow>
              ) : (
                paginatedPolicies.map((policy) => (
                  <TableRow key={policy.id} hover>
                    <TableCell>{policy.policyName}</TableCell>
											<TableCell>
                      <Chip label={policy.policyType} size="small" variant="outlined" />
											</TableCell>
                    <TableCell>{policy.effectiveDate}</TableCell>
                    <TableCell>{policy.expiryDate}</TableCell>
											<TableCell>
                      <Chip
                        label={policy.status}
                        size="small"
                        sx={getStatusStyle(policy.status)}
                      />
											</TableCell>
											<TableCell>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleView(policy)}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(policy)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(policy.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
											</TableCell>
										</TableRow>
                ))
              )}
								</TableBody>
							</Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPolicies.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          
        />
      </Paper>

      {/* Edit/Add Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          {selectedPolicy ? 'Edit Policy' : 'Add Policy'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Policy Name *"
                value={newPolicy.policyName}
                onChange={(e) => setNewPolicy({ ...newPolicy, policyName: e.target.value })}
                error={Boolean(errors.policyName)}
                helperText={errors.policyName}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.policyType)}>
                <InputLabel>Policy Type *</InputLabel>
                <Select
                  value={newPolicy.policyType}
                  onChange={(e) => setNewPolicy({ ...newPolicy, policyType: e.target.value })}
                  label="Policy Type *"
                >
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="IT">IT</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                  <MenuItem value="Security">Security</MenuItem>
									</Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newPolicy.description}
                onChange={(e) => setNewPolicy({ ...newPolicy, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Effective Date *"
                value={newPolicy.effectiveDate}
                onChange={(e) => setNewPolicy({ ...newPolicy, effectiveDate: e.target.value })}
                error={Boolean(errors.effectiveDate)}
                helperText={errors.effectiveDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={newPolicy.expiryDate}
                onChange={(e) => setNewPolicy({ ...newPolicy, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newPolicy.status}
                  onChange={(e) => setNewPolicy({ ...newPolicy, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
				</DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
							Cancel
						</Button>
          <Button
            variant="contained"
            onClick={handleSavePolicy}
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Saving...' : 'Save'}
						</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Policy Details</DialogTitle>
        <DialogContent>
          {selectedPolicy && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Policy Name:</strong> {selectedPolicy.policyName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Type:</strong> {selectedPolicy.policyType}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Description:</strong> {selectedPolicy.description}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Effective Date:</strong> {selectedPolicy.effectiveDate}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Expiry Date:</strong> {selectedPolicy.expiryDate}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Status:</strong>{' '}
                <Chip label={selectedPolicy.status} size="small" sx={getStatusStyle(selectedPolicy.status)} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Created Date:</strong> {selectedPolicy.createdDate}
              </Typography>
            </Box>
          )}
				</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
        </DialogActions>
			</Dialog>
    </Box>
	);
};

export default Policy;
